import {
  COMPANY_RULE_LABELS,
  COMPANY_RULES,
  DEFAULT_PERFORMANCE_SETTINGS,
  formatDateTime,
  performanceSettingsInputSchema,
  performanceSettingsSchema,
  TRAFFIC_LIGHT_LABELS,
  trafficLight,
  type CompanyRuleName,
  type PerformanceSettings,
} from '@fluxus/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Lock } from 'lucide-react';
import { useId, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import { toast } from 'sonner';
import { PageContainer, PageHeader } from '@/components/layout/PageHeader';
import { AlertSettingsSection } from '@/features/alerts/AlertSettingsSection';
import { ErrorState, LoadingState } from '@/components/states/States';
import { StatusBadge } from '@/components/status/StatusBadge';
import { Button } from '@/components/ui/button';
import { FormAlert, FormField, fieldA11y } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { errorMessage } from '@/lib/api/errors';
import { useAuth, useCan, useCurrentUser } from '@/lib/auth/session';
import { apiFieldErrors, hasFieldErrors, zodFieldErrors, type FieldErrors } from '@/lib/forms';

export const performanceSettingsKey = (organizationId: string) => ['performance-settings', organizationId];
const formatPercent = (v: number) => `${v.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`;

/** Exemplos da prévia: como as faixas classificam (inclui o 90,67% do exemplo do PRD). */
const SAMPLES: { label: string; performance: number | null; future?: boolean; direction?: 'RANGE' }[] = [
  { label: '104,8% (acima da meta)', performance: 104.8 },
  { label: '96%', performance: 96 },
  { label: '90,67% (R$ 2,72 mi de R$ 3 mi)', performance: 90.67 },
  { label: '82%', performance: 82 },
  { label: '70%', performance: 70 },
  { label: 'Faixa ideal: 96% (fora da faixa)', performance: 96, direction: 'RANGE' },
  { label: 'Sem resultado lançado', performance: null },
  { label: 'Período futuro', performance: null, future: true },
];

/**
 * Configurações › Performance e semáforo (T-061, RF-50, RN-10): faixas do semáforo, regra da
 * performance da empresa e teto; limites dos alertas (T-110b). Todos da equipe veem; só o Admin Fluxus altera.
 */
export function PerformancePage() {
  const { organizationId = '' } = useParams();
  const { client } = useAuth();
  const user = useCurrentUser();
  const can = useCan();
  const settings = useQuery({
    queryKey: performanceSettingsKey(organizationId),
    queryFn: ({ signal }) =>
      client.request(`/organizations/${organizationId}/performance-settings`, {
        schema: performanceSettingsSchema,
        signal,
      }),
  });

  return (
    <PageContainer className="max-w-3xl">
      <PageHeader
        title="Performance e semáforo"
        description="Como a performance é classificada no semáforo, como a performance geral da empresa é calculada a partir das perspectivas e quando os alertas de desvio são gerados."
      />
      {settings.isPending ? (
        <LoadingState className="mt-6" rows={4} label="Carregando os parâmetros" />
      ) : settings.isError ? (
        <ErrorState
          className="mt-6"
          error={settings.error}
          onRetry={() => void settings.refetch()}
          retrying={settings.isFetching}
        />
      ) : (
        <PerformanceSettingsForm
          key={settings.data.updatedAt ?? 'padrao'}
          organizationId={organizationId}
          initial={settings.data}
        />
      )}
      <div className="mt-6">
        <AlertSettingsSection
          organizationId={organizationId}
          canEdit={user.organization.isFluxus && can('administracao', 'edit')}
        />
      </div>
    </PageContainer>
  );
}

export function PerformanceSettingsForm({
  organizationId,
  initial,
}: {
  organizationId: string;
  initial: PerformanceSettings;
}) {
  const { client } = useAuth();
  const user = useCurrentUser();
  const can = useCan();
  const queryClient = useQueryClient();
  // RF-50: parâmetros são do Admin Fluxus.
  const canEdit = user.organization.isFluxus && can('administracao', 'edit');
  const [greenMin, setGreenMin] = useState(String(initial.greenMin));
  const [yellowMin, setYellowMin] = useState(String(initial.yellowMin));
  const [companyRule, setCompanyRule] = useState<CompanyRuleName>(initial.companyRule);
  const [performanceCap, setPerformanceCap] = useState(String(initial.performanceCap));
  const [requireRedJustification, setRequireRedJustification] = useState(initial.requireRedJustification);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const ids = { green: useId(), yellow: useId(), rule: useId(), cap: useId() };

  const parsedNumber = (v: string) => Number(v.trim().replace(',', '.'));
  // A prévia usa o que está no formulário (o mesmo motor do cálculo de verdade).
  const previewRule = {
    greenMin: Number.isFinite(parsedNumber(greenMin)) ? parsedNumber(greenMin) : initial.greenMin,
    yellowMin: Number.isFinite(parsedNumber(yellowMin)) ? parsedNumber(yellowMin) : initial.yellowMin,
  };
  const isDefault =
    initial.updatedAt === null ||
    (initial.greenMin === DEFAULT_PERFORMANCE_SETTINGS.greenMin &&
      initial.yellowMin === DEFAULT_PERFORMANCE_SETTINGS.yellowMin &&
      initial.companyRule === DEFAULT_PERFORMANCE_SETTINGS.companyRule &&
      initial.performanceCap === DEFAULT_PERFORMANCE_SETTINGS.performanceCap &&
      initial.requireRedJustification === DEFAULT_PERFORMANCE_SETTINGS.requireRedJustification);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (saving) return;
    setFormError(null);
    const parsed = performanceSettingsInputSchema.safeParse({
      greenMin: greenMin.trim().replace(',', '.'),
      yellowMin: yellowMin.trim().replace(',', '.'),
      companyRule,
      performanceCap: performanceCap.trim(),
      requireRedJustification,
    });
    if (!parsed.success) return setErrors(zodFieldErrors(parsed.error));
    setErrors({});
    setSaving(true);
    try {
      const saved = await client.request(`/organizations/${organizationId}/performance-settings`, {
        method: 'PUT',
        body: parsed.data,
        schema: performanceSettingsSchema,
      });
      queryClient.setQueryData(performanceSettingsKey(organizationId), saved);
      toast.success('Parâmetros salvos');
    } catch (err) {
      const fields = apiFieldErrors(err);
      if (hasFieldErrors(fields)) setErrors(fields);
      else setFormError(err);
    } finally {
      setSaving(false);
    }
  }

  const restoreDefaults = () => {
    setGreenMin(String(DEFAULT_PERFORMANCE_SETTINGS.greenMin));
    setYellowMin(String(DEFAULT_PERFORMANCE_SETTINGS.yellowMin));
    setCompanyRule(DEFAULT_PERFORMANCE_SETTINGS.companyRule);
    setPerformanceCap(String(DEFAULT_PERFORMANCE_SETTINGS.performanceCap));
    setRequireRedJustification(DEFAULT_PERFORMANCE_SETTINGS.requireRedJustification);
    setErrors({});
  };

  return (
    <form noValidate onSubmit={submit} className="mt-6 flex flex-col gap-6">
      {!canEdit ? (
        <p className="flex items-center gap-2 rounded-lg border border-border bg-muted px-4 py-3 text-sm">
          <Lock className="size-4 shrink-0 text-muted-foreground" aria-hidden />
          Só o Admin Fluxus altera estes parâmetros.
        </p>
      ) : null}
      <p className="text-sm text-muted-foreground">
        {isDefault
          ? 'Valem os padrões sugeridos pela Fluxus (Anexo B).'
          : `Alterado em ${formatDateTime(initial.updatedAt!)}.`}
      </p>

      <section aria-label="Faixas do semáforo" className="rounded-lg border border-border bg-surface p-5">
        <h2 className="text-base font-bold">Faixas do semáforo</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Valem para todos os indicadores, menos os que têm regra própria. Em “menor melhor” e “faixa ideal” a
          performance já considera a direção, então as mesmas faixas se aplicam.
        </p>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <FormField id={ids.green} label="Verde a partir de (% da meta)" error={errors.greenMin}>
            <Input
              {...fieldA11y(ids.green, errors.greenMin)}
              value={greenMin}
              inputMode="decimal"
              maxLength={6}
              disabled={!canEdit}
              onChange={(e) => setGreenMin(e.target.value)}
              className="h-11 w-32"
            />
          </FormField>
          <FormField id={ids.yellow} label="Amarelo a partir de (% da meta)" error={errors.yellowMin}>
            <Input
              {...fieldA11y(ids.yellow, errors.yellowMin)}
              value={yellowMin}
              inputMode="decimal"
              maxLength={6}
              disabled={!canEdit}
              onChange={(e) => setYellowMin(e.target.value)}
              className="h-11 w-32"
            />
          </FormField>
        </div>
        <ul className="mt-4 flex flex-col gap-1.5 text-sm">
          <li className="flex flex-wrap items-center gap-2">
            <StatusBadge status="red" label={TRAFFIC_LIGHT_LABELS.red} />
            abaixo de {formatPercent(previewRule.yellowMin)}
          </li>
          <li className="flex flex-wrap items-center gap-2">
            <StatusBadge status="gray" label={TRAFFIC_LIGHT_LABELS.gray} />
            sem resultado ou sem meta no período
          </li>
          <li className="flex flex-wrap items-center gap-2">
            <StatusBadge status="blue" label={TRAFFIC_LIGHT_LABELS.blue} />
            período futuro
          </li>
        </ul>
      </section>

      <section aria-label="Como fica" className="rounded-lg border border-border bg-surface p-5">
        <h2 className="text-base font-bold">Como fica</h2>
        <ul aria-label="Prévia do semáforo" className="mt-3 flex flex-col divide-y divide-border">
          {SAMPLES.map((s) => {
            const light = trafficLight(s.performance, previewRule, s.direction ?? 'HIGHER_BETTER', s.future);
            return (
              <li key={s.label} className="flex items-center justify-between gap-3 py-2 text-sm">
                <span>{s.label}</span>
                <StatusBadge status={light} label={TRAFFIC_LIGHT_LABELS[light]} />
              </li>
            );
          })}
        </ul>
      </section>

      <section aria-label="Performance da empresa" className="rounded-lg border border-border bg-surface p-5">
        <h2 className="text-base font-bold">Performance da empresa e limites</h2>
        <div className="mt-4 grid gap-4 sm:grid-cols-2">
          <FormField id={ids.rule} label="Regra da performance da empresa" error={errors.companyRule}>
            <NativeSelect
              {...fieldA11y(ids.rule, errors.companyRule)}
              value={companyRule}
              disabled={!canEdit}
              onChange={(e) => setCompanyRule(e.target.value as CompanyRuleName)}
            >
              {COMPANY_RULES.map((r) => (
                <option key={r} value={r}>
                  {COMPANY_RULE_LABELS[r]}
                </option>
              ))}
            </NativeSelect>
          </FormField>
          <FormField
            id={ids.cap}
            label="Teto de performance (%)"
            error={errors.performanceCap}
            hint={
              <p className="text-xs text-muted-foreground">
                Nenhum indicador passa disso (evita que um resultado fora da curva distorça a média).
              </p>
            }
          >
            <Input
              {...fieldA11y(ids.cap, errors.performanceCap, true)}
              value={performanceCap}
              inputMode="numeric"
              maxLength={4}
              disabled={!canEdit}
              onChange={(e) => setPerformanceCap(e.target.value)}
              className="h-11 w-32"
            />
          </FormField>
        </div>
        <label className="mt-4 flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 size-4 shrink-0 accent-(--brand)"
            checked={requireRedJustification}
            disabled={!canEdit}
            onChange={(e) => setRequireRedJustification(e.target.checked)}
          />
          <span>
            <span className="font-medium">Justificativa obrigatória quando o realizado fica vermelho</span>
            <span className="block text-xs text-muted-foreground">
              Ao lançar um resultado que deixa o mês do indicador no vermelho, quem lança explica o desvio.
            </span>
          </span>
        </label>
        <p className="mt-4 text-sm text-muted-foreground">
          Os pesos ficam onde cada item é cadastrado: perspectivas em{' '}
          <Link
            to={`/empresas/${organizationId}/estrategia/perspectivas`}
            className="font-semibold text-brand-strong underline-offset-4 hover:underline dark:text-brand"
          >
            Perspectivas
          </Link>
          , objetivos na{' '}
          <Link
            to={`/empresas/${organizationId}/estrategia/construcao`}
            className="font-semibold text-brand-strong underline-offset-4 hover:underline dark:text-brand"
          >
            Construção do BSC
          </Link>{' '}
          e indicadores no cadastro de{' '}
          <Link
            to={`/empresas/${organizationId}/indicadores/kpis`}
            className="font-semibold text-brand-strong underline-offset-4 hover:underline dark:text-brand"
          >
            KPIs
          </Link>
          . Sem peso definido, os itens dividem igualmente o que sobra; itens sem dados ficam fora e os demais
          são reponderados.
        </p>
      </section>

      {formError ? <FormAlert tone="error">{errorMessage(formError)}</FormAlert> : null}
      {canEdit ? (
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="outline" onClick={restoreDefaults} disabled={saving}>
            Voltar aos padrões
          </Button>
          <Button type="submit" loading={saving}>
            Salvar parâmetros
          </Button>
        </div>
      ) : null}
    </form>
  );
}
